import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Button,
  Box,
  Stack,
  Text,
  Divider,
  Input,
  Autocomplete,
  Chip,
} from '@ltvco/refresh-lib/theme';
import { nameize, useStatesOptionsAbbr } from '@ltvco/refresh-lib/utils';
import { RapidsInput, RapidsSearchData, Record } from '@ltvco/refresh-lib/v1';
import { AdvancedSearchURLParams, CollectionType } from '@ltvco/refresh-lib/v2';
import { Close, Tune } from '@mui/icons-material';
import { SetStateAction, Dispatch, useContext } from 'react';

import { ReportColumnContainer, AgeInput } from 'components/ChatBV/components';
import {
  CHATBV_GA4_EVENTS,
  FILTERS_BY_COLLECTION_TYPE,
} from 'components/ChatBV/constants';

type FiltersColumnProps = {
  currentWidth: string;
  isMobileBreakpoint: boolean;
  onClose: () => void;
  searchParams: AdvancedSearchURLParams;
  setSearchParams: Dispatch<SetStateAction<AdvancedSearchURLParams>>;
  handleOpenTeaser: (initialTeaserData?: Record[]) => void;
  collectionType: CollectionType;
};

const RapidsInputStyle = {
  '& > .MuiAutocomplete-root': {
    width: '100%',
    '& > .address-input-container': {
      '& > .address-input': {
        width: '100%',
        '& > .rapids-search-icon': {
          display: 'none',
        },
      },
    },
  },
};

export const FiltersColumn = ({
  currentWidth,
  isMobileBreakpoint,
  onClose,
  searchParams,
  setSearchParams,
  handleOpenTeaser,
  collectionType,
}: FiltersColumnProps) => {
  const { trackEventGA4 } = useContext(AppConfig);
  const states = Object.values(useStatesOptionsAbbr())
    .map((state) => state.display)
    .filter((state) => state !== 'All');

  const handleSubmit = () => {
    if (searchParams.age) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_age,
      });
    }
    if (searchParams.state) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_state,
      });
    }
    if (searchParams.city) {
      trackEventGA4({
        event_name: CHATBV_GA4_EVENTS.name,
        type: CHATBV_GA4_EVENTS.report_section_interactions.type,
        subtype:
          CHATBV_GA4_EVENTS.report_section_interactions.subTypes
            .filters_submit_city,
      });
    }
    handleOpenTeaser();
  };

  const handleSearchLocation = (propertyData: RapidsSearchData) => {
    const { city, state } = propertyData;
    const data = {
      city,
      state,
    };
    setSearchParams({ ...searchParams, ...data });
  };

  const handleStateSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchParams({ ...searchParams, state: value });
  };

  const renderFilterChips = () => {
    const filterChips = FILTERS_BY_COLLECTION_TYPE[collectionType]
      .filter((filter) => searchParams[filter as keyof AdvancedSearchURLParams])
      .map((filter) => (
        <Chip
          key={filter}
          label={`${nameize(filter)}: ${
            searchParams[filter as keyof AdvancedSearchURLParams]
          }`}
          onDelete={() => {
            setSearchParams({
              ...searchParams,
              [filter as keyof AdvancedSearchURLParams]: '',
            });
          }}
        />
      ));
    if (filterChips.length > 0) {
      return (
        <Stack direction="row" spacing={1}>
          {filterChips}
        </Stack>
      );
    }
    return null;
  };

  // const handleMonthSelect = (value: string, type: 'birth' | 'death') => {
  //   setSearchParams({
  //     ...searchParams,
  //     [type === 'birth' ? 'birthMonth' : 'deathMonth']: value,
  //   });
  // };

  return (
    <ReportColumnContainer
      spacing={4}
      minWidth={currentWidth}
      width="30%"
      height="98%"
      sx={{
        p: 2,

        ...(isMobileBreakpoint && {
          animation: 'fadeInScale 0.4s ease-out',
          '@keyframes fadeInScale': {
            '0%': {
              transform: 'scale(0.95)',
              opacity: 0,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        }),
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          backgroundColor: 'background.paper',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Tune />
          <Text variant="body1" fontWeight="bold" color="text.primary">
            Filters
          </Text>
        </Box>
        <Close
          onClick={onClose}
          sx={{ color: 'text.secondary', cursor: 'pointer' }}
        />
      </Stack>
      <Text color="text.secondary">
        Search for possible personal details, such as addresses, phone numbers,
        and associates
      </Text>
      {renderFilterChips()}
      <Stack spacing={3} flexGrow={1}>
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.fullName}
          label="Full Name"
          data-cy="full_name_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, fullName: e.target.value })
          }
          variant="outlined"
          disabled={true}
        />
        <Stack direction="row" spacing={2}>
          <RapidsInput
            parentFormSubmitHandler={handleSearchLocation}
            searchTypes={['city']}
            includeCounties={false}
            updateEmptyOptions
            valueOveride={searchParams.city}
            inputOptions={{
              placeholder: 'City',
              hideSearchButton: true,
              hideInputLabel: true,
              InputProps: {
                sx: {
                  borderRadius: '4px',
                },
              },
              autoFocus: false,
              customStyles: RapidsInputStyle,
            }}
            disableCloseOnSelect={true}
            data-cy="city_field"
            inputLabel="City"
          />
          <Autocomplete
            id="state"
            data-cy="state_header_field"
            options={states}
            value={searchParams.state}
            renderInput={(params) => (
              <Input
                {...params}
                label="State"
                variant="outlined"
                name="state"
                data-cy="state_report_field"
                onInput={handleStateSelect}
                onChange={handleStateSelect}
              />
            )}
            onChange={(_event, data) =>
              setSearchParams({ ...searchParams, state: data })
            }
            isOptionEqualToValue={(option, optionValue) =>
              optionValue === '' || option === optionValue
            }
          />
        </Stack>
        <AgeInput
          currentAge={
            searchParams.age ? Number.parseInt(searchParams.age, 10) : 0
          }
          onChange={(value) =>
            setSearchParams({ ...searchParams, age: value.toString() })
          }
        />
        {/* <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: '50%' }}>
            <InputLabel
              id="birthMonth"
              shrink={Boolean(searchParams.birthMonth)}
              sx={{ top: searchParams.birthMonth ? '0px' : '-7px' }}
            >
              Birth Month
            </InputLabel>
            <Select
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline legend': {
                  width: searchParams.birthMonth ? 'auto' : '0px',
                  overflow: 'hidden',
                },
              }}
              value={searchParams.birthMonth}
              options={monthOptions}
              id="birthMonth"
              data-cy="birth_month_select"
              MenuProps={{
                disablePortal: true,
              }}
              label="Birth Month"
              labelId="birthMonth"
              onOptionSelect={(value) => handleMonthSelect(value, 'birth')}
            />
          </FormControl>
          <Input
            placeholder="Year"
            sx={{ width: '50%' }}
            type="number"
            label="Year"
            value={searchParams.birthYear}
            onChange={(e) =>
              setSearchParams({
                ...searchParams,
                birthYear: e?.target?.value || '',
              })
            }
          />
        </Stack> */}
        {/* <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: '50%' }}>
            <InputLabel
              id="deathMonth"
              shrink={Boolean(searchParams.deathMonth)}
              sx={{ top: searchParams.deathMonth ? '0px' : '-7px' }}
            >
              Death Month
            </InputLabel>
            <Select
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-notchedOutline legend': {
                  width: searchParams.deathMonth ? 'auto' : '0px',
                  overflow: 'hidden',
                },
              }}
              value={searchParams.deathMonth}
              options={monthOptions}
              id="deathMonth"
              data-cy="death_month_select"
              MenuProps={{
                disablePortal: true,
              }}
              label="Death Month"
              labelId="deathMonth"
              onOptionSelect={(value) => handleMonthSelect(value, 'death')}
            />
          </FormControl>
          <Input
            placeholder="Year"
            sx={{ width: '50%' }}
            type="number"
            label="Year"
            value={searchParams.deathYear}
            onChange={(e) =>
              setSearchParams({
                ...searchParams,
                deathYear: e?.target?.value || '',
              })
            }
          />
        </Stack> */}
        {/* <Text variant="body1" fontWeight="bold" color="text.primary">
          Professional Search
        </Text>
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.title}
          label="Job Title"
          data-cy="title_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, title: e.target.value })
          }
          variant="outlined"
        />
        <Input
          fullWidth
          autoFocus={false}
          value={searchParams.company}
          label="Company"
          data-cy="company_field"
          slotProps={{
            input: {
              sx: {
                borderRadius: '4px',
              },
            },
          }}
          onChange={(e) =>
            setSearchParams({ ...searchParams, company: e.target.value })
          }
          variant="outlined"
        />
        <Autocomplete
          fullWidth
          autoFocus={false}
          value={searchParams.industry}
          options={contactIndustries}
          data-cy="industry_field"
          renderInput={(params) => (
            <Input {...params} label="Industry" variant="outlined" />
          )}
          onChange={(_event, data) =>
            setSearchParams({ ...searchParams, industry: data })
          }
          isOptionEqualToValue={(option, optionValue) =>
            optionValue === '' || option === optionValue
          }
        />
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Seniority
        </Text>
        <Stack>
          {contactSeniorityLevels.map((level) => (
            <FormControlLabel
              key={level.value}
              control={
                <Checkbox
                  checked={searchParams.level.includes(level.value)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newLevels = [...searchParams.level, level.value];
                      setSearchParams({ ...searchParams, level: newLevels });
                    } else {
                      const newLevels = searchParams.level.filter(
                        (l: string) => l !== level.value
                      );
                      setSearchParams({ ...searchParams, level: newLevels });
                    }
                  }}
                />
              }
              label={level.label}
            />
          ))}
        </Stack>
        <Text variant="body1" fontWeight="bold" color="text.primary">
          Employee Count
        </Text>
        <Stack>
          <Slider
            getAriaLabel={(index) =>
              index === 0 ? 'Min Employee Count' : 'Max Employee Count'
            }
            name="employee-count"
            data-cy="employee_count_slider"
            min={1}
            max={9}
            value={[
              searchParams.company_size_min
                ? Number.parseInt(searchParams.company_size_min, 10)
                : 1,
              searchParams.company_size_max
                ? Number.parseInt(searchParams.company_size_max, 10)
                : 9,
            ]}
            valueLabelDisplay="auto"
            marks={contactEmployeeCountRanges.map((range) => ({
              value: range.value,
            }))}
            step={null}
            onChange={(_, value) => {
              if (Array.isArray(value)) {
                setSearchParams({
                  ...searchParams,
                  company_size_min: value[0]?.toString() ?? '',
                  company_size_max: value[1]?.toString() ?? '',
                });
              }
            }}
            valueLabelFormat={(value: number) => findEmployeeCountLabel(value)}
          />
          <Text
            variant="caption"
            sx={{ textAlign: 'center' }}
            data-cy="employee_count_label"
          >
            Min {findEmployeeCountLabel(searchParams.company_size_min)} - Max{' '}
            {findEmployeeCountLabel(searchParams.company_size_max)}
          </Text>
        </Stack> */}
      </Stack>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          Show Results
        </Button>
      </Box>
    </ReportColumnContainer>
  );
};
